import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import DeleteIcon from "@mui/icons-material/Delete";
import { makeStyles } from "@mui/styles";

import ConfirmBox from "../general/confirmBox";
import { deleteOrigin } from "../../api/origin";

const useStyles = makeStyles(() => ({
  tableContainer: {
    width: "auto",
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "calc(100vh - 230px)",
    marginTop: "-70px",
  },
  tableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "var(--primary-light-blue-color)",
      color: "#000",
    },
    "& td": {
      padding: "2px 4px 2px 10px",
    },
  },
  headerRow: {
    backgroundColor: "var(--primary-blue-color)",
    color: "#ffffff !important",
    "& th": {
      padding: "9px",
      color: "#fff",
    },
  },
  oddRow: {
    backgroundColor: "#eeeeee",
    opacity: "100%",
  },
  evenRow: {
    backgroundColor: "#dddddd",
    opacity: "100%",
  },
}));

/**
 * Expecting origin fields to be lowercase:
 * origin.id, origin.name, origin.contacts (array), plus legacy
 * origin.contactfirstname, origin.email as a fallback.
 */

// Returns the origin's contacts, falling back to the legacy flat
// fields (contactfirstname/email) if this origin hasn't been
// re-saved under the new contacts table yet.
const getOriginContactsList = (origin) => {
  if (Array.isArray(origin.contacts) && origin.contacts.length > 0) {
    return origin.contacts;
  }

  if (origin.contactfirstname || origin.email) {
    return [
      {
        firstname: origin.contactfirstname || "",
        lastname: "",
        email: origin.email || "",
      },
    ];
  }

  return [];
};

const originContactMatchesSearch = (contact, q) => {
  const fullName = `${contact.firstname || ""} ${contact.lastname || ""}`
    .trim()
    .toLowerCase();
  return (
    fullName.includes(q) ||
    (contact.email && contact.email.toLowerCase().includes(q))
  );
};

const OriginList = ({ origins, searchText, onOriginSelect }) => {
  const classes = useStyles();

  const loading = useSelector((state) => state.origins?.loading);
  const error = useSelector((state) => state.origins?.error);

  const itemsPerPage = 10;

  const [page, setPage] = useState(1);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  // keep a stack like your original code: newest first
  const [selectedOriginId, setSelectedOriginId] = useState([]); // e.g. [idToDelete, ...previous]
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState("");

  const q = (searchText || "").toLowerCase();

  // reset page when searching
  useEffect(() => {
    setPage(1);
  }, [q]);

  const allOrigins = origins?.origins || [];

  const filteredOrigins = useMemo(() => {
    const deletedSet = new Set(selectedOriginId); // hide rows after user clicks delete
    return allOrigins
      .filter((o) => !deletedSet.has(o?.id))
      .filter((o) => {
        const name = (o?.name || "").toLowerCase();
        if (name.includes(q)) return true;
        return getOriginContactsList(o).some((contact) =>
          originContactMatchesSearch(contact, q)
        );
      });
  }, [allOrigins, q, selectedOriginId]);

  const totalPages = Math.max(1, Math.ceil(filteredOrigins.length / itemsPerPage));

  // clamp current page if results shrink
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const handlePageChange = (_event, newPage) => setPage(newPage);

  const handleCloseSnackbar = () => setShowMessage(false);

  const handleCloseConfirmDelete = () => {
    setOpenConfirmDelete(false);
    // remove the top item (the one we're confirming)
    setSelectedOriginId((prev) => prev.filter((_, idx) => idx !== 0));
  };

  const deleteOriginById = async (id) => {
    try {
      await deleteOrigin(id);
      setMessage("Origin deleted successfully");
      setShowMessage(true);
    } catch (err) {
      setMessage(err?.response?.data?.message || "Delete failed");
      setShowMessage(true);
      // revert the optimistic hide if delete fails
      setSelectedOriginId((prev) => prev.filter((x) => x !== id));
    }
  };

  // ✅ IMPORTANT: show loading/error BEFORE "No origin found"
  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <div className="alert alert-danger">Error: {String(error)}</div>;
  }

  if (!filteredOrigins || filteredOrigins.length === 0) {
    return (
      <div className="no-orders-found-text">
        <h3>No origin found</h3>

        {showMessage && (
          <Snackbar
            open={showMessage}
            autoHideDuration={1200}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{ "& .MuiSnackbar-root": { bottom: "50px" } }}
            message="Done"
          >
            <Alert
              severity={message.toLowerCase().includes("success") ? "success" : "error"}
              variant="filled"
            >
              {message}
            </Alert>
          </Snackbar>
        )}
      </div>
    );
  }

  return (
    <div>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell>Name</TableCell>
              <TableCell>Contact Name</TableCell>
              <TableCell>Email Address</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredOrigins.slice(startIndex, endIndex).map((origin, index) => {
              const contactsList = getOriginContactsList(origin);
              const primaryContact = contactsList[0] || {};
              const extraContacts = contactsList.length - 1;
              const primaryName = `${primaryContact.firstname || ""} ${
                primaryContact.lastname || ""
              }`.trim();

              return (
                <TableRow
                  key={origin.id}
                  onClick={() => onOriginSelect(origin)}
                  className={`${classes.tableRow} ${
                    origin.isSelected
                      ? "highlighted-row"
                      : index % 2 === 0
                      ? classes.evenRow
                      : classes.oddRow
                  }`}
                >
                  <TableCell>{origin.name}</TableCell>
                  <TableCell>
                    {primaryName}
                    {extraContacts > 0 ? ` (+${extraContacts} more)` : ""}
                  </TableCell>
                  <TableCell>{primaryContact.email}</TableCell>

                  <TableCell>
                    <IconButton
                      aria-label="delete"
                      style={{ color: "#990000" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOriginId((prev) => [origin.id, ...prev]); // push stack
                        setOpenConfirmDelete(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        renderItem={(item) => (
          <PaginationItem component="button" type="button" {...item} />
        )}
      />

      {showMessage && (
        <Snackbar
          open={showMessage}
          autoHideDuration={1200}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ "& .MuiSnackbar-root": { bottom: "50px" } }}
          onClose={handleCloseSnackbar}
          message="Done"
        >
          <Alert
            severity={message.toLowerCase().includes("success") ? "success" : "error"}
            variant="filled"
          >
            {message}
          </Alert>
        </Snackbar>
      )}

      {openConfirmDelete && (
        <ConfirmBox
          confirmAction={() => {
            const idToDelete = selectedOriginId[0];
            if (idToDelete) deleteOriginById(idToDelete);
            setOpenConfirmDelete(false);
          }}
          cancelAction={handleCloseConfirmDelete}
        />
      )}
    </div>
  );
};

export default OriginList;

